import React from "react";
import styled from "styled-components";
import StyledTable from "../../components/StyledTable";
import StyledEvent from "../../components/StyledEvent";
import MultiEvent from "../../components/MultiEvent";
import LoadingSpinner from "../../components/LoadingSpinner";
import { StyledText, TextSize } from "../../components/Text";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ExploreIcon from "@mui/icons-material/Explore";
import ShieldIcon from "@mui/icons-material/Shield";
import BalanceIcon from "@mui/icons-material/Balance";
import TuneIcon from "@mui/icons-material/Tune";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import ScienceIcon from "@mui/icons-material/Science";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { set } from "date-fns";
import { SemesterContext } from "../../context/SemesterContext";
import { UserContext } from "../../context/UserContext";
import { UniversityContext } from "../../context/UniversityContext";

const MAX_CREDIT_LOAD = 60;

const PersonalizedContainer = styled.div`
  height: 100%;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  color: #ffffff;
  background: #000920;
  padding: 24px;
  box-sizing: border-box;
`;

const PersonalizeSettings = styled.div`
  display: flex;
  flex-direction: column;
  padding: 28px 32px 24px;
  background: linear-gradient(180deg, #0a0f29 0%, #050a1f 100%);
  border-radius: 16px;
  border: 1px solid #1c2240;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.5);
`;

const StepRow = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 24px;
  align-items: flex-start;
  padding: 24px 0;
  border-bottom: 1px solid #1c2240;

  &:last-of-type {
    border-bottom: none;
  }
`;

const StepHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding-top: 8px;
`;

const StepBadge = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(197, 75, 77, 0.18);
  border: 1px solid rgba(197, 75, 77, 0.55);
  color: #f1a4a5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const StepTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepTitle = styled.h4`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const StepSubtitle = styled.span`
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #8b8fa8;
  line-height: 1.45;
`;

const OptionGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ $columns }) => $columns || 5},
    minmax(0, 168px)
  );
  justify-content: flex-start;
  gap: 12px;
`;

const OptionCard = styled.button<{ $active: boolean; $compact?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ $compact }) => ($compact ? "center" : "flex-start")};
  gap: 8px;
  padding: ${({ $compact }) => ($compact ? "18px 12px" : "20px 14px")};
  background: ${({ $active }) =>
    $active ? "rgba(197, 75, 77, 0.12)" : "rgba(255, 255, 255, 0.02)"};
  border: 1.5px solid ${({ $active }) => ($active ? "#c54b4d" : "#1c2240")};
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
  font-family: Inter, sans-serif;
  text-align: center;
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: ${({ $active }) => ($active ? "#c54b4d" : "#3a3f5c")};
    background: ${({ $active }) =>
    $active ? "rgba(197, 75, 77, 0.18)" : "rgba(255, 255, 255, 0.04)"};
  }
`;

const OptionIconWrap = styled.div<{ $active: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ $active }) =>
    $active ? "rgba(197, 75, 77, 0.2)" : "rgba(255, 255, 255, 0.04)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $active }) => ($active ? "#e88587" : "#9ea2bd")};
`;

const OptionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const OptionDescription = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: #8b8fa8;
  line-height: 1.35;
`;

const CheckBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #c54b4d;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 8px rgba(197, 75, 77, 0.5);
`;

const NumberCard = styled.button<{ $active: boolean }>`
  position: relative;
  height: 84px;
  background: ${({ $active }) =>
    $active ? "rgba(197, 75, 77, 0.12)" : "rgba(255, 255, 255, 0.02)"};
  border: 1.5px solid ${({ $active }) => ($active ? "#c54b4d" : "#1c2240")};
  border-radius: 12px;
  color: #ffffff;
  font-family: Inter, sans-serif;
  font-size: 30px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:hover {
    border-color: ${({ $active }) => ($active ? "#c54b4d" : "#3a3f5c")};
  }
`;

const CustomWeightsBlock = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid #1c2240;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.02);
`;

const WeightFieldsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 200px));
  justify-content: flex-start;
  gap: 28px;
  margin-top: 4px;
`;

const WeightFieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-right: 10px;
`;

const WeightFieldLabel = styled.label`
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #b0b4d8;
`;

const WeightDescription = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 1.4;
  color: #b0b4d8;
`;

const WeightSumIndicator = styled.div<{ $isValid: boolean }>`
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $isValid }) => ($isValid ? "#4ade80" : "#f87171")};
`;

const CustomWeightInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #2a2f4a;
  background: #0a0f29;
  color: #fff;
  font-family: Inter, sans-serif;
  font-size: 14px;
  &::placeholder {
    color: #555a73;
  }
`;

const GenerateRow = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 24px;
  padding-top: 20px;
`;

const GenerateContent = styled.div`
  grid-column: 2;
  display: flex;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-width: 708px;
  max-width: 888px;
  padding: 14px 36px;
  background: linear-gradient(180deg, #2868ff 0%, #1645d7 100%);
  border: 1px solid #2f66ff;
  border-radius: 12px;
  color: #fff;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(47, 102, 255, 0.3);
  transition: opacity 0.18s ease, transform 0.18s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GOAL_OPTIONS = [
  {
    id: "fast_track",
    title: "Fast Track",
    description: "Heavily weights graduation progress",
    icon: RocketLaunchIcon,
  },
  {
    id: "explore_passions",
    title: "Explore Passions",
    description: "Heavily weights student interest",
    icon: ExploreIcon,
  },
  {
    id: "play_it_safe",
    title: "Play it Safe",
    description: "Heavily weights prior grades",
    icon: ShieldIcon,
  },
  {
    id: "balanced",
    title: "Balanced",
    description: "Standard equal weighting",
    icon: BalanceIcon,
  },
  {
    id: "custom",
    title: "Custom",
    description: "Enter your own weighting preferences",
    icon: TuneIcon,
  },
];

const DEFAULT_MIN_FIT_SCORE = "0.25";
const GOAL_MIN_FIT_SCORE_DEFAULTS: Record<string, string> = {
  fast_track: "0.12",
  explore_passions: "0.15",
  play_it_safe: "0.50",
  balanced: "0.25",
};

export const getDefaultMinFitScore = (goalId: string): string =>
  GOAL_MIN_FIT_SCORE_DEFAULTS[goalId] ?? DEFAULT_MIN_FIT_SCORE;

const THEME_OPTIONS = [
  { id: "code", title: "Coding", icon: LaptopMacIcon },
  { id: "science", title: "Science", icon: ScienceIcon },
  { id: "games", title: "Games", icon: SportsEsportsIcon },
  { id: "business", title: "Business", icon: BusinessCenterIcon },
  { id: "none", title: "None", icon: BlockIcon },
];

type PersonalizedRoadMapSectionProps = {
  isPersonalizedGenerated: boolean;
  isGeneratingPersonalized: boolean;
  personalizedNoDataMessage: string;
  weightType: string;
  competencyWeight: string;
  setCompetencyWeight: (value: string) => void;
  interestWeight: string;
  setInterestWeight: (value: string) => void;
  progressWeight: string;
  setProgressWeight: (value: string) => void;
  maxCredits: number;
  setMaxCredits: (value: number) => void;
  minFitScore: string;
  setMinFitScore: (value: string) => void;
  personalizedGenerateDisabled: boolean;
  preferredTheme: string;
  maxSets: number;
  personalizedRoadmapData: Array<any>;
  selectedPersonalizedRoadmap: any;
  dates: Array<any>;
  personalizedRequiredWidth: string;
  personalizedOptionalWidth: string;
  personalizedRequiredEvents: Array<any>;
  personalizedOptionalEvents: Array<any>;
  setWeightType: (value: string) => void;
  setPreferredTheme: (value: string) => void;
  setMaxSets: (value: number) => void;
  setIsPersonalizedGenerated: (value: boolean) => void;
  generatePersonalizedRecommendations: () => void;
  mapPersonalizedData: (roadmap: any) => void;
  onConfirm: () => void;
  onEventClick: (event: any) => void;
};

function PersonalizedRoadMapSection({
  isPersonalizedGenerated,
  isGeneratingPersonalized,
  personalizedNoDataMessage,
  weightType,
  competencyWeight,
  setCompetencyWeight,
  interestWeight,
  setInterestWeight,
  progressWeight,
  setProgressWeight,
  maxCredits,
  setMaxCredits,
  minFitScore,
  setMinFitScore,
  personalizedGenerateDisabled,
  preferredTheme,
  maxSets,
  personalizedRoadmapData,
  selectedPersonalizedRoadmap,
  dates,
  personalizedRequiredWidth,
  personalizedOptionalWidth,
  personalizedRequiredEvents,
  personalizedOptionalEvents,
  setWeightType,
  setPreferredTheme,
  setMaxSets,
  setIsPersonalizedGenerated,
  generatePersonalizedRecommendations,
  mapPersonalizedData,
  onConfirm,
  onEventClick,
}: PersonalizedRoadMapSectionProps) {
  const clampWeight = (raw: string): string => {
    if (raw.trim() === "") return "";
    const n = Number(raw);
    if (!Number.isFinite(n)) return raw;
    if (n > 1) return "1";
    if (n < 0) return "0";
    return raw;
  };

  const parseWeightNumber = (s: string): number => {
    const t = s.trim();
    if (t === "") return 0;
    const n = Number(t);
    return Number.isFinite(n) ? n : 0;
  };

  const weightSum =
    parseWeightNumber(competencyWeight) +
    parseWeightNumber(interestWeight) +
    parseWeightNumber(progressWeight);
  const roundedSum = Math.round(weightSum * 1e6) / 1e6;
  const isSumValid = roundedSum === 1;

  const isMaxCreditsInvalid =
    !Number.isFinite(maxCredits) ||
    maxCredits < 1 ||
    maxCredits > MAX_CREDIT_LOAD;

  const semesterContext = React.useContext(SemesterContext);
  const userContext = React.useContext(UserContext);
  const universityContext = React.useContext(UniversityContext);

  const currentSemester = semesterContext.selectedSemester;
  const currentStudent = userContext.user;
  const selectedProgram = universityContext.selectedProgram;

  if (!currentSemester?.is_open) {
    return (
      <PersonalizedContainer><PersonalizeSettings>
        <StyledText style={{ marginBottom: "12px", fontSize: "24px", fontWeight: 700 }}>
          Cannot generate personalized road maps
        </StyledText>
        <StyledText color="#8b8fa8" style={{ fontSize: "16px" }}>
          Personalized recommendations are only available for the current semester
        </StyledText></PersonalizeSettings>
      </PersonalizedContainer>
    );
  }
  else if (currentSemester?.semester_name === currentStudent?.programs?.find((p: any) => p?.program_name === selectedProgram?.label)?.term_entered) {
    return (
      <PersonalizedContainer>
        <PersonalizeSettings>
          <StyledText style={{ marginBottom: "12px", fontSize: "24px", fontWeight: 700 }}>
            Cannot generate personalized road maps
          </StyledText>
          <StyledText color="#8b8fa8" style={{ fontSize: "16px" }}>
            Student is in their first semester, so personalized recommendations cannot be generated.
          </StyledText></PersonalizeSettings>
      </PersonalizedContainer>
    );
  }

  return (
    <PersonalizedContainer>
      {!isPersonalizedGenerated ? (
        <PersonalizeSettings>
          <StepRow>
            <StepHeader>
              <StepBadge>1</StepBadge>
              <StepTitleBlock>
                <StepTitle>Choose your goal</StepTitle>
                <StepSubtitle>
                  What's your primary focus right now?
                </StepSubtitle>
              </StepTitleBlock>
            </StepHeader>
            <div>
              <OptionGrid $columns={5}>
                {GOAL_OPTIONS.map((goal) => {
                  const isActive = weightType === goal.id;
                  const GoalIcon = goal.icon;
                  return (
                    <OptionCard
                      key={goal.id}
                      type="button"
                      $active={isActive}
                      onClick={() => {
                        setWeightType(goal.id);
                        setMinFitScore(getDefaultMinFitScore(goal.id));
                      }}
                    >
                      <OptionIconWrap $active={isActive}>
                        <GoalIcon sx={{ fontSize: 22 }} />
                      </OptionIconWrap>
                      <OptionTitle>{goal.title}</OptionTitle>
                      <OptionDescription>{goal.description}</OptionDescription>
                      {isActive && (
                        <CheckBadge>
                          <CheckIcon sx={{ fontSize: 14 }} />
                        </CheckBadge>
                      )}
                    </OptionCard>
                  );
                })}
              </OptionGrid>
              {weightType === "custom" && (
                <CustomWeightsBlock>
                  <WeightDescription>
                    Total of the three weights must equal <strong>1</strong>.
                    The sum cannot exceed or be less than 1.
                  </WeightDescription>
                  <WeightSumIndicator $isValid={isSumValid}>
                    Current total: {roundedSum}
                    {isSumValid ? " (OK)" : " (must equal 1)"}
                  </WeightSumIndicator>
                  <WeightFieldsRow>
                    <WeightFieldRow>
                      <WeightFieldLabel htmlFor="competency_weight">
                        Competency Weight
                      </WeightFieldLabel>
                      <CustomWeightInput
                        id="competency_weight"
                        type="number"
                        step="any"
                        inputMode="decimal"
                        value={competencyWeight}
                        onChange={(e) =>
                          setCompetencyWeight(clampWeight(e.target.value))
                        }
                        min={0}
                        max={1}
                        placeholder="e.g. 0.33"
                        aria-label="Competency weight"
                      />
                    </WeightFieldRow>
                    <WeightFieldRow>
                      <WeightFieldLabel htmlFor="interest_weight">
                        Interest Weight
                      </WeightFieldLabel>
                      <CustomWeightInput
                        id="interest_weight"
                        type="number"
                        step="any"
                        inputMode="decimal"
                        value={interestWeight}
                        onChange={(e) =>
                          setInterestWeight(clampWeight(e.target.value))
                        }
                        min={0}
                        max={1}
                        placeholder="e.g. 0.33"
                        aria-label="Interest weight"
                      />
                    </WeightFieldRow>
                    <WeightFieldRow>
                      <WeightFieldLabel htmlFor="progress_weight">
                        Progress Weight
                      </WeightFieldLabel>
                      <CustomWeightInput
                        id="progress_weight"
                        type="number"
                        step="any"
                        inputMode="decimal"
                        value={progressWeight}
                        onChange={(e) =>
                          setProgressWeight(clampWeight(e.target.value))
                        }
                        min={0}
                        max={1}
                        placeholder="e.g. 0.34"
                        aria-label="Progress weight"
                      />
                    </WeightFieldRow>
                  </WeightFieldsRow>
                </CustomWeightsBlock>
              )}
            </div>
          </StepRow>

          <StepRow>
            <StepHeader>
              <StepBadge>2</StepBadge>
              <StepTitleBlock>
                <StepTitle>Personal theme</StepTitle>
                <StepSubtitle>
                  What kind of topics excite you most?
                </StepSubtitle>
              </StepTitleBlock>
            </StepHeader>
            <OptionGrid $columns={5}>
              {THEME_OPTIONS.map((theme) => {
                const isActive = preferredTheme === theme.id;
                const ThemeIcon = theme.icon;
                return (
                  <OptionCard
                    key={theme.id}
                    type="button"
                    $active={isActive}
                    $compact
                    onClick={() => setPreferredTheme(theme.id)}
                  >
                    <OptionIconWrap $active={isActive}>
                      <ThemeIcon sx={{ fontSize: 22 }} />
                    </OptionIconWrap>
                    <OptionTitle>{theme.title}</OptionTitle>
                    {isActive && (
                      <CheckBadge>
                        <CheckIcon sx={{ fontSize: 14 }} />
                      </CheckBadge>
                    )}
                  </OptionCard>
                );
              })}
            </OptionGrid>
          </StepRow>

          <StepRow>
            <StepHeader>
              <StepBadge>3</StepBadge>
              <StepTitleBlock>
                <StepTitle>Number of roadmaps</StepTitle>
                <StepSubtitle>
                  How many options would you like to see?
                </StepSubtitle>
              </StepTitleBlock>
            </StepHeader>
            <OptionGrid $columns={4}>
              {[1, 2, 3, 4].map((num) => {
                const isActive = maxSets === num;
                return (
                  <NumberCard
                    key={num}
                    type="button"
                    $active={isActive}
                    onClick={() => setMaxSets(num)}
                  >
                    {num}
                    {isActive && (
                      <CheckBadge>
                        <CheckIcon sx={{ fontSize: 14 }} />
                      </CheckBadge>
                    )}
                  </NumberCard>
                );
              })}
            </OptionGrid>
          </StepRow>
          <StepRow>
            <StepHeader>
              <StepBadge>4</StepBadge>
              <StepTitleBlock>
                <StepTitle>Maximum Credit</StepTitle>
                <StepSubtitle>
                  How many credits would you like to take?
                </StepSubtitle>
              </StepTitleBlock>
            </StepHeader>
            <WeightFieldsRow>
              <WeightFieldRow>
                <WeightFieldLabel htmlFor="max_credit">
                  Enter Maximum Credit
                </WeightFieldLabel>
                <CustomWeightInput
                  id="max_credit"
                  type="number"
                  step="any"
                  min={1}
                  max={MAX_CREDIT_LOAD}

                  value={Number.isFinite(maxCredits) ? maxCredits : ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setMaxCredits(raw === "" ? NaN : Number(raw));
                  }}
                  placeholder="e.g. 60"
                  aria-label="Maximum credit"
                />
                {isMaxCreditsInvalid && (
                  <WeightSumIndicator $isValid={false}>
                    Maximum credit must be between 1 and {MAX_CREDIT_LOAD}.
                  </WeightSumIndicator>
                )}
              </WeightFieldRow></WeightFieldsRow>
          </StepRow>

          <StepRow>
            <StepHeader>
              <StepBadge>5</StepBadge>
              <StepTitleBlock>
                <StepTitle>Minimum Fit Score</StepTitle>
                <StepSubtitle>
                  Only show recommendations at or above this score (optional).
                </StepSubtitle>
              </StepTitleBlock>
            </StepHeader>
            <WeightFieldsRow>
              <WeightFieldRow>
                <WeightFieldLabel htmlFor="min_fit_score">
                  Enter Minimum Fit Score
                </WeightFieldLabel>
                <CustomWeightInput
                  id="min_fit_score"
                  type="number"
                  step="any"
                  inputMode="decimal"
                  min={0}
                  max={1}
                  value={minFitScore}
                  onChange={(e) => setMinFitScore(clampWeight(e.target.value))}
                  placeholder="e.g. 0.5"
                  aria-label="Minimum fit score"
                />
              </WeightFieldRow></WeightFieldsRow>
          </StepRow>

          <GenerateRow>
            <GenerateContent>
              {isGeneratingPersonalized ? (
                <LoadingSpinner />
              ) : (
                <ActionButton
                  type="button"
                  onClick={generatePersonalizedRecommendations}
                  disabled={personalizedGenerateDisabled}
                >
                  <LightbulbIcon sx={{ fontSize: 20 }} />
                  Generate My Recommendations
                </ActionButton>
              )}
            </GenerateContent>
          </GenerateRow>

        </PersonalizeSettings>
      ) : (
        <>
          <StyledTable
            setIsPersonalizedGenerated={setIsPersonalizedGenerated}
            isPersonalizedGenerated={isPersonalizedGenerated}
            data={personalizedRoadmapData}
            selectedData={selectedPersonalizedRoadmap}
            dateRange={dates}
            timeline={false}
            requiredWidth={personalizedRequiredWidth}
            optionalWidth={personalizedOptionalWidth}
            reMapData={mapPersonalizedData}
            onConfirm={onConfirm}
          >
            {personalizedRequiredEvents.map((event: any, key: number) => (
              <>
                {!event.isMulti ? (
                  <StyledEvent
                    key={key}
                    onItemClick={() => onEventClick(event)}
                    title={event.title}
                    description={event.description}
                    startDate={event.start_date || event.startDate}
                    endDate={event.target_completion_date || event.endDate}
                    left={event.left}
                    top={event.top}
                    height={event.height}
                    backgroundColor={event.backgroundColor || "#0045ff"}
                    fontColor={event.fontColor}
                    skills={event.skills}
                    credits={event.credits}
                    competency_code={event.competency_code}
                    fitScore={event.fit_score}
                    reason={event.reason}
                    showPersonalizedFit
                  />
                ) : (
                  <MultiEvent eventData={event} showPersonalizedFit />
                )}
              </>
            ))}
            {personalizedOptionalEvents.map((event: any, key: number) => (
              <>
                {!event.isMulti ? (
                  <StyledEvent
                    key={key}
                    onItemClick={() => onEventClick(event)}
                    title={event.title}
                    description={event.description}
                    startDate={event.start_date || event.startDate}
                    endDate={event.target_completion_date || event.endDate}
                    left={event.left}
                    top={event.top}
                    height={event.height}
                    backgroundColor={event.backgroundColor || "#0045ff"}
                    fontColor={event.fontColor}
                    skills={event.skills}
                    credits={event.credits}
                    competency_code={event.competency_code}
                    fitScore={event.fit_score}
                    reason={event.reason}
                    showPersonalizedFit
                  />
                ) : (
                  <MultiEvent eventData={event} showPersonalizedFit />
                )}
              </>
            ))}
          </StyledTable>
        </>
      )}
    </PersonalizedContainer>
  );
}

export default PersonalizedRoadMapSection;
